/**
 * The desk's internal notes about a customer or an order (§6.12).
 *
 * **Internal, always.** There is no portal route that serves a note, and this
 * component is not exported to the portal's application at all — it is
 * mounted by the back office, beside a customer's file and beside a claim.
 *
 * A note carries a service and a priority because the offer sells them, and
 * it can be marked handled because a note is as often a task as a record:
 * "call about the overdue invoice before releasing this order" is not
 * something to accumulate. One that cannot be closed is one the next person
 * scrolls past.
 */

import { useCallback, useMemo, useState } from "react";
import { useTranslation } from "react-i18next";
import { useCan } from "@hardauto/core-access";
import type { ApiClient } from "@hardauto/core-api";
import { useAction, useResource } from "@hardauto/core-data";
import { moment } from "@hardauto/core-i18n";
import { Field } from "@hardauto/core-forms";
import {
  Badge,
  Button,
  Card,
  Checkbox,
  EmptyState,
  LoadingState,
  Select,
  Textarea,
  useToast,
} from "@hardauto/core-ui";

import { SupportApi, type NotePriority, type NoteServiceCode, type NoteSubject } from "./api";
import { NAMESPACE } from "./i18n";
import { PRIORITIES, PRIORITY_TONE, SERVICES } from "./status";
import "./screens/support.css";

export interface NotesPanelProps {
  client: ApiClient;
  subjectKind: NoteSubject;
  subjectId: string;
}

export function NotesPanel({ client, subjectKind, subjectId }: NotesPanelProps) {
  const { t, i18n } = useTranslation(NAMESPACE);
  const locale = i18n.language;
  const api = useMemo(() => new SupportApi(client), [client]);
  const action = useAction();
  const toast = useToast();
  const mayWrite = useCan("support.note.write");

  const [openOnly, setOpenOnly] = useState(false);
  const [body, setBody] = useState("");
  const [service, setService] = useState<NoteServiceCode>("adv");
  const [priority, setPriority] = useState<NotePriority>("normal");

  const fetcher = useCallback(
    () =>
      api.notes({
        subject_kind: subjectKind,
        subject_id: subjectId,
        ...(openOnly ? { open_only: true } : {}),
      }),
    [api, subjectKind, subjectId, openOnly],
  );
  const notes = useResource(fetcher);

  return (
    <Card
      title={t("notes.title")}
      description={t("notes.description")}
      actions={
        <Checkbox
          label={t("notes.openOnly")}
          checked={openOnly}
          onChange={(event) => setOpenOnly(event.target.checked)}
        />
      }
    >
      {notes.loading ? (
        <LoadingState label={t("notes.loading")} />
      ) : (notes.data?.items ?? []).length === 0 ? (
        <EmptyState title={t("notes.empty")} />
      ) : (
        <ul className="hardauto-support__notes">
          {(notes.data?.items ?? []).map((note) => (
            <li
              key={note.id}
              className="hardauto-support__note"
              data-handled={note.handled_at ? "true" : undefined}
            >
              <div className="hardauto-support__note-head">
                <Badge tone="info">{t(`service.${note.service}`)}</Badge>
                {/* The priority is shown only when it is one: a "normale"
                    badge on every note is a column of noise that makes the
                    urgent one harder to see. */}
                {note.priority !== "normal" ? (
                  <Badge tone={PRIORITY_TONE[note.priority]}>
                    {t(`priority.${note.priority}`)}
                  </Badge>
                ) : null}
                <span className="hardauto-cell__muted">
                  {t("notes.author", {
                    name: note.author_name || EMPTY_AUTHOR,
                    date: moment(note.created_at, locale),
                  })}
                </span>
                {note.handled_at ? (
                  <span className="hardauto-cell__muted">
                    {t("notes.handledAt", { date: moment(note.handled_at, locale) })}
                  </span>
                ) : null}
              </div>
              <p className="hardauto-support__note-body">{note.body}</p>
              {mayWrite ? (
                <div className="hardauto-support__note-actions">
                  {note.handled_at ? null : (
                    <Button
                      size="sm"
                      busy={action.isBusy(`handle:${note.id}`)}
                      busyLabel={t("notes.handled")}
                      onClick={() =>
                        void action.run(`handle:${note.id}`, async () => {
                          await api.handleNote(note.id);
                          toast.show(t("notes.handledDone"));
                          notes.reload();
                        })
                      }
                    >
                      {t("notes.handled")}
                    </Button>
                  )}
                  <Button
                    size="sm"
                    busy={action.isBusy(`delete:${note.id}`)}
                    busyLabel={t("notes.delete")}
                    onClick={() =>
                      void action.run(`delete:${note.id}`, async () => {
                        await api.deleteNote(note.id);
                        toast.show(t("notes.deleted"));
                        notes.reload();
                      })
                    }
                  >
                    {t("notes.delete")}
                  </Button>
                </div>
              ) : null}
            </li>
          ))}
        </ul>
      )}

      {mayWrite ? (
        <div className="hardauto-support__composer">
          <Field label={t("notes.body")}>
            {(props) => (
              <Textarea
                {...props}
                rows={2}
                placeholder={t("notes.bodyPlaceholder")}
                value={body}
                onChange={(event) => setBody(event.target.value)}
              />
            )}
          </Field>
          <div className="hardauto-support__note-fields">
            <Field label={t("notes.service")}>
              {(props) => (
                <Select
                  {...props}
                  value={service}
                  onChange={(event) => setService(event.target.value as NoteServiceCode)}
                >
                  {SERVICES.map((value) => (
                    <option key={value} value={value}>
                      {t(`service.${value}`)}
                    </option>
                  ))}
                </Select>
              )}
            </Field>
            <Field label={t("notes.priority")}>
              {(props) => (
                <Select
                  {...props}
                  value={priority}
                  onChange={(event) => setPriority(event.target.value as NotePriority)}
                >
                  {PRIORITIES.map((value) => (
                    <option key={value} value={value}>
                      {t(`priority.${value}`)}
                    </option>
                  ))}
                </Select>
              )}
            </Field>
          </div>
          <Button
            variant="primary"
            disabled={!body.trim()}
            busy={action.isBusy("add")}
            busyLabel={t("notes.adding")}
            onClick={() =>
              void action.run("add", async () => {
                try {
                  await api.addNote({
                    subject_kind: subjectKind,
                    subject_id: subjectId,
                    service,
                    priority,
                    body: body.trim(),
                  });
                  setBody("");
                  toast.show(t("notes.added"), { tone: "success" });
                  notes.reload();
                } catch {
                  toast.show(t("notes.failed"), { tone: "danger" });
                }
              })
            }
          >
            {t("notes.add")}
          </Button>
        </div>
      ) : null}
    </Card>
  );
}

/**
 * What stands in for an author whose account has been closed.
 *
 * The name is stored on the note, so this is only reached for a note written
 * before that column existed. An empty string would render the separator with
 * nothing before it.
 */
const EMPTY_AUTHOR = "—";
